import {
    AlertCircle,
    ArrowLeft,
    CheckCircle2,
    Clock,
    DollarSign,
    Loader2,
    Receipt,
    TrendingDown,
    Wallet,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import AuthenticatedLayout from '../components/AuthenticatedLayout'
import DatePicker from '../components/DatePicker'
import { useUserProfile } from '../context/UserProfileContext'
import { ApiError } from '../utils/api'
import selfAssessmentService from '../utils/selfAssessmentService'

const ninoPattern = /^[A-Z]{2}[0-9]{6}[A-Z]$/i

const normalizeNino = (value: string): string => value.replace(/\s+/g, '').toUpperCase()

const formatCurrency = (amount?: number): string => {
    if (amount === undefined || amount === null) return '£0.00'
    return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
    }).format(amount)
}

const formatDate = (value?: string) => {
    if (!value) return '—'
    try {
        const date = new Date(value)
        if (Number.isNaN(date.getTime())) return value
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        })
    } catch {
        return value
    }
}

type BalanceData = {
    balanceDetails?: Record<string, unknown>
    overdueAmounts?: Record<string, unknown>
    payableAmount?: Record<string, unknown>
    pendingCharges?: Array<Record<string, unknown>>
    transactions?: Array<Record<string, unknown>>
}

type PaymentData = {
    payments?: Array<Record<string, unknown>>
}

export default function SelfAssessmentAccountPage() {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const ninoFromUrl = searchParams.get('nino') || ''

    const [nino, setNino] = useState(ninoFromUrl)
    const [fromDate, setFromDate] = useState('')
    const [toDate, setToDate] = useState('')
    const [balanceData, setBalanceData] = useState<BalanceData | null>(null)
    const [paymentsData, setPaymentsData] = useState<PaymentData | null>(null)
    const [activeView, setActiveView] = useState<'balance' | 'payments'>('balance')
    const [isLoadingBalance, setIsLoadingBalance] = useState(false)
    const [isLoadingPayments, setIsLoadingPayments] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [successMessage, setSuccessMessage] = useState<string | null>(null)

    useUserProfile()
    useEffect(() => {
        if (ninoFromUrl) {
            setNino(normalizeNino(ninoFromUrl))
        }
    }, [ninoFromUrl])

    // Set default date range (current tax year)
    const setDefaultDates = () => {
        const now = new Date()
        const currentYear = now.getFullYear()
        const currentMonth = now.getMonth()

        // UK tax year runs April 6 to April 5
        const taxYearStart = currentMonth >= 3 ? currentYear : currentYear - 1

        setFromDate(`${taxYearStart}-04-06`)
        setToDate(`${taxYearStart + 1}-04-05`)
    }

    const handleLoadBalance = async () => {
        const normalized = normalizeNino(nino)

        setError(null)
        setSuccessMessage(null)

        if (!ninoPattern.test(normalized)) {
            setError('Enter a valid National Insurance number (e.g. AB123456C).')
            return
        }

        // Ensure dates are in YYYY-MM-DD format
        const formattedFromDate = fromDate && fromDate.includes('-') ? fromDate : fromDate ? new Date(fromDate).toISOString().split('T')[0] : undefined
        const formattedToDate = toDate && toDate.includes('-') ? toDate : toDate ? new Date(toDate).toISOString().split('T')[0] : undefined

        try {
            setIsLoadingBalance(true)
            const { response } = await selfAssessmentService.getBalanceAndTransactions(
                normalized,
                {
                    fromDate: formattedFromDate,
                    toDate: formattedToDate,
                    onlyOpenItems: false,
                    includeLocks: true,
                }
            )

            setBalanceData(response as BalanceData)
            setActiveView('balance')
            setSuccessMessage('Account balance loaded successfully.')
        } catch (err) {
            console.error('Failed to load balance:', err)
            if (err instanceof ApiError) {
                if (err.status === 401) {
                    setError('HMRC authorization required. Please authorize with HMRC first.')
                } else {
                    // Extract nested error message
                    const errorMessage = err.response?.message || err.message || 'Unable to load account balance.'
                    setError(errorMessage)
                }
            } else if (err instanceof Error) {
                setError(err.message)
            } else {
                setError('Unable to load account balance.')
            }
            setBalanceData(null)
        } finally {
            setIsLoadingBalance(false)
        }
    }

    const handleLoadPayments = async () => {
        const normalized = normalizeNino(nino)

        setError(null)
        setSuccessMessage(null)

        if (!ninoPattern.test(normalized)) {
            setError('Enter a valid National Insurance number (e.g. AB123456C).')
            return
        }

        if (!fromDate || !toDate) {
            setError('Date range is required for payments. Please set from and to dates.')
            return
        }

        // Validate date format and ensure proper format for API
        const formattedFromDate = fromDate.includes('-') ? fromDate : new Date(fromDate).toISOString().split('T')[0]
        const formattedToDate = toDate.includes('-') ? toDate : new Date(toDate).toISOString().split('T')[0]

        try {
            setIsLoadingPayments(true)
            const { response } = await selfAssessmentService.getPaymentsAndAllocations(
                normalized,
                {
                    fromDate: formattedFromDate,
                    toDate: formattedToDate,
                }
            )

            setPaymentsData(response as PaymentData)
            setActiveView('payments')
            setSuccessMessage('Payments loaded successfully.')
        } catch (err) {
            console.error('Failed to load payments:', err)
            if (err instanceof ApiError) {
                if (err.status === 401) {
                    setError('HMRC authorization required. Please authorize with HMRC first.')
                } else {
                    // Extract nested error message
                    const errorMessage = err.response?.message || err.message || 'Unable to load payments.'
                    setError(errorMessage)
                }
            } else if (err instanceof Error) {
                setError(err.message)
            } else {
                setError('Unable to load payments.')
            }
            setPaymentsData(null)
        } finally {
            setIsLoadingPayments(false)
        }
    }

    const renderBalanceView = () => {
        if (!balanceData) {
            return (
                <div className="text-center py-12 text-gray-500">
                    <Wallet className="mx-auto h-12 w-12 mb-4 text-gray-300" />
                    <p>Load your account balance to view details.</p>
                </div>
            )
        }

        // Extract balance details with proper type checking
        const balanceDetails = balanceData.balanceDetails as any
        const overdueAmount = (balanceData.overdueAmounts as any)?.amount || (balanceDetails?.overdueAmounts as any)?.amount
        const payableAmount = (balanceData.payableAmount as any)?.amount || (balanceDetails?.payableAmount as any)?.amount
        const pendingCharges = (balanceData.pendingCharges || balanceDetails?.pendingCharges || []) as any[]
        const transactions = (balanceData.transactions || balanceDetails?.transactions || []) as any[]

        return (
            <div className="space-y-6">
                {/* Balance Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-gradient-to-br from-red-50 to-red-100 border border-red-200 rounded-lg p-6">
                        <div className="flex items-center gap-2 mb-2">
                            <TrendingDown className="h-5 w-5 text-red-600" />
                            <h3 className="text-sm font-semibold text-gray-900">Overdue</h3>
                        </div>
                        <p className="text-2xl font-bold text-red-600">{formatCurrency(overdueAmount)}</p>
                        <p className="text-xs text-red-800 mt-1">Immediate payment required</p>
                    </div>

                    <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 border border-yellow-200 rounded-lg p-6">
                        <div className="flex items-center gap-2 mb-2">
                            <Clock className="h-5 w-5 text-yellow-600" />
                            <h3 className="text-sm font-semibold text-gray-900">Payable</h3>
                        </div>
                        <p className="text-2xl font-bold text-yellow-600">{formatCurrency(payableAmount)}</p>
                        <p className="text-xs text-yellow-800 mt-1">Due soon</p>
                    </div>

                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-6">
                        <div className="flex items-center gap-2 mb-2">
                            <Receipt className="h-5 w-5 text-blue-600" />
                            <h3 className="text-sm font-semibold text-gray-900">Pending Charges</h3>
                        </div>
                        <p className="text-2xl font-bold text-blue-600">{pendingCharges.length}</p>
                        <p className="text-xs text-blue-800 mt-1">Awaiting processing</p>
                    </div>
                </div>

                {/* Pending Charges */}
                {pendingCharges.length > 0 && (
                    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                            <h3 className="text-base font-semibold text-gray-900">Pending Charges</h3>
                        </div>
                        <div className="divide-y divide-gray-200">
                            {pendingCharges.map((charge, index) => {
                                const chargeType = charge.chargeType || charge.mainType || 'Charge'
                                const chargeRef = charge.chargeReference || charge.reference
                                const mainTrans = charge.mainTransaction || charge.mainTrans
                                const subTrans = charge.subTransaction || charge.subTrans
                                const amount = charge.amount || charge.originalAmount || 0
                                const dueDate = charge.dueDate || charge.paymentDate
                                
                                return (
                                    <div key={index} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                                        <div className="flex justify-between items-start mb-2">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <Clock className="h-4 w-4 text-yellow-600" />
                                                    <p className="text-sm font-semibold text-gray-900">
                                                        {String(chargeType)}
                                                    </p>
                                                </div>
                                                <div className="space-y-1">
                                                    {chargeRef && (
                                                        <p className="text-xs text-gray-600 font-mono">
                                                            Ref: {String(chargeRef)}
                                                        </p>
                                                    )}
                                                    {dueDate && (
                                                        <p className="text-xs text-gray-600">
                                                            Due: {formatDate(String(dueDate))}
                                                        </p>
                                                    )}
                                                    {(mainTrans || subTrans) && (
                                                        <p className="text-xs text-gray-500">
                                                            Transaction: {mainTrans || ''}{subTrans ? ` / ${subTrans}` : ''}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-lg font-bold text-yellow-600">
                                                    {formatCurrency(Number(amount))}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                )}

                {/* Transactions */}
                {transactions.length > 0 && (
                    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                            <h3 className="text-base font-semibold text-gray-900">
                                Transaction History ({String(transactions.length)})
                            </h3>
                        </div>
                        <div className="divide-y divide-gray-200">
                            {transactions.slice(0, 15).map((transaction, index) => {
                                const transType = transaction.transactionType || transaction.type || 'Transaction'
                                const transDate = transaction.transactionDate || transaction.date || transaction.effectiveDate
                                const amount = transaction.amount || transaction.outstandingAmount || transaction.originalAmount || 0
                                const transId = transaction.transactionId || transaction.chargeReference
                                const items = transaction.items || []
                                
                                return (
                                    <div key={index} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                                        <div className="flex justify-between items-start">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <Receipt className="h-4 w-4 text-blue-600" />
                                                    <p className="text-sm font-semibold text-gray-900">
                                                        {String(transType)}
                                                    </p>
                                                </div>
                                                <div className="space-y-1">
                                                    {transDate && (
                                                        <p className="text-xs text-gray-600">
                                                            {formatDate(String(transDate))}
                                                        </p>
                                                    )}
                                                    {transId && (
                                                        <p className="text-xs text-gray-500 font-mono">
                                                            ID: {String(transId)}
                                                        </p>
                                                    )}
                                                </div>
                                                {items.length > 0 && (
                                                    <div className="mt-2 ml-4 space-y-1">
                                                        {items.slice(0, 3).map((item: any, idx: number) => (
                                                            <div key={idx} className="text-xs text-gray-600">
                                                                • {item.subItemType || item.type}: {formatCurrency(item.amount)}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="text-right">
                                                <p className="text-base font-bold text-gray-900">
                                                    {formatCurrency(Number(amount))}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                        {transactions.length > 15 && (
                            <div className="px-6 py-3 bg-gray-50 text-center text-sm text-gray-600">
                                Showing 15 of {transactions.length} transactions
                            </div>
                        )}
                    </div>
                )}

                {/* Additional Balance Details */}
                {balanceDetails && Object.keys(balanceDetails).length > 0 && (
                    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                            <h3 className="text-base font-semibold text-gray-900">Complete Balance Information</h3>
                            <p className="text-sm text-gray-600 mt-1">All account details from HMRC</p>
                        </div>
                        <div className="px-6 py-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {Object.entries(balanceDetails).map(([key, value]) => {
                                    // Skip already displayed nested objects
                                    if (key === 'transactions' || key === 'pendingCharges') return null
                                    
                                    // Format the key into readable label
                                    const label = key
                                        .replace(/([A-Z])/g, ' $1')
                                        .replace(/^./, str => str.toUpperCase())
                                        .trim()
                                    
                                    // Handle different value types
                                    let displayValue: string
                                    
                                    if (value === null || value === undefined) {
                                        displayValue = '—'
                                    } else if (typeof value === 'object' && !Array.isArray(value)) {
                                        // Handle nested objects like overdueAmounts, payableAmount
                                        const obj = value as any
                                        if (obj.amount !== undefined) {
                                            displayValue = formatCurrency(obj.amount)
                                            if (obj.dueDate) {
                                                displayValue += ` (Due: ${formatDate(obj.dueDate)})`
                                            }
                                        } else {
                                            // Display object keys and values
                                            displayValue = Object.entries(obj)
                                                .map(([k, v]) => {
                                                    const subLabel = k.replace(/([A-Z])/g, ' $1').toLowerCase()
                                                    if (typeof v === 'number' && (k.toLowerCase().includes('amount') || k.toLowerCase().includes('balance'))) {
                                                        return `${subLabel}: ${formatCurrency(v)}`
                                                    }
                                                    return `${subLabel}: ${v}`
                                                })
                                                .join(', ')
                                        }
                                    } else if (Array.isArray(value)) {
                                        displayValue = `${value.length} items`
                                    } else if (typeof value === 'number' && (key.toLowerCase().includes('amount') || key.toLowerCase().includes('balance'))) {
                                        displayValue = formatCurrency(value)
                                    } else if (typeof value === 'boolean') {
                                        displayValue = value ? 'Yes' : 'No'
                                    } else if (key.toLowerCase().includes('date')) {
                                        displayValue = formatDate(String(value))
                                    } else {
                                        displayValue = String(value)
                                    }
                                    
                                    return (
                                        <div key={key} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                                            <p className="text-xs font-medium text-gray-600 mb-1">{label}</p>
                                            <p className="text-sm font-semibold text-gray-900 break-words">{displayValue}</p>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        )
    }

    const renderPaymentsView = () => {
        if (!paymentsData) {
            return (
                <div className="text-center py-12 text-gray-500">
                    <DollarSign className="mx-auto h-12 w-12 mb-4 text-gray-300" />
                    <p>Load payments to view details.</p>
                </div>
            )
        }

        const payments = paymentsData.payments || []
        
        // Calculate total payment amount
        const totalAmount = payments.reduce((sum, payment) => {
            const amount = payment.paymentAmount || payment.amount || 0
            return sum + (typeof amount === 'number' ? amount : 0)
        }, 0)

        return (
            <div className="space-y-6">
                {/* Payments Summary */}
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-6">
                    <div className="flex items-center gap-2 mb-4">
                        <Receipt className="h-5 w-5 text-blue-600" />
                        <h3 className="text-lg font-semibold text-gray-900">Payment History</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                            <p className="text-xs text-gray-600 mb-1">Total Payments</p>
                            <p className="text-2xl font-bold text-gray-900">{payments.length}</p>
                        </div>
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                            <p className="text-xs text-gray-600 mb-1">Total Amount</p>
                            <p className="text-2xl font-bold text-green-600">{formatCurrency(totalAmount)}</p>
                        </div>
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                            <p className="text-xs text-gray-600 mb-1">Period</p>
                            <p className="text-sm font-medium text-gray-900">
                                {formatDate(fromDate)}
                            </p>
                            <p className="text-xs text-gray-500">to</p>
                            <p className="text-sm font-medium text-gray-900">
                                {formatDate(toDate)}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Payments List */}
                {payments.length > 0 ? (
                    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                            <h3 className="text-base font-semibold text-gray-900">Transaction Details</h3>
                        </div>
                        <div className="divide-y divide-gray-200">
                            {payments.map((payment, index) => {
                                const paymentId = payment.paymentId || payment.paymentLot || payment.transactionId
                                const paymentType = payment.paymentType || payment.type || 'Payment'
                                const amount = payment.paymentAmount || payment.amount || 0
                                const paymentDate = payment.paymentDate || payment.date
                                const paymentMethod = payment.paymentMethod || payment.method
                                const allocations = payment.allocations || []
                                
                                return (
                                    <div key={index} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                                        <div className="flex justify-between items-start mb-3">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <DollarSign className="h-4 w-4 text-green-600" />
                                                    <p className="text-sm font-semibold text-gray-900">
                                                        {String(paymentType)}
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-4 text-xs text-gray-600">
                                                    <div className="flex items-center gap-1">
                                                        <Clock className="h-3 w-3" />
                                                        <span>{formatDate(paymentDate as string)}</span>
                                                    </div>
                                                    {paymentId ? (
                                                        <div className="flex items-center gap-1">
                                                            <span className="font-mono text-gray-500">
                                                                ID: {String(paymentId)}
                                                            </span>
                                                        </div>
                                                    ) : null}
                                                    {paymentMethod ? (
                                                        <div className="px-2 py-0.5 bg-gray-100 rounded text-gray-700">
                                                            {String(paymentMethod)}
                                                        </div>
                                                    ) : null}
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-lg font-bold text-green-600">
                                                    {formatCurrency(amount as number)}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Allocations */}
                                        {Array.isArray(allocations) && allocations.length > 0 && (
                                            <div className="mt-3 ml-6 pl-4 border-l-2 border-gray-200">
                                                <p className="text-xs font-medium text-gray-700 mb-2">Allocations:</p>
                                                <div className="space-y-2">
                                                    {allocations.map((allocation: any, allocIndex: number) => (
                                                        <div key={allocIndex} className="flex justify-between items-center text-xs bg-gray-50 rounded px-3 py-2">
                                                            <div>
                                                                <p className="font-medium text-gray-900">
                                                                    {allocation.chargeType || allocation.type || 'Charge'}
                                                                </p>
                                                                {allocation.chargeReference && (
                                                                    <p className="text-gray-500 font-mono text-[10px]">
                                                                        {String(allocation.chargeReference)}
                                                                    </p>
                                                                )}
                                                                {allocation.periodDescription && (
                                                                    <p className="text-gray-600 mt-0.5">
                                                                        {String(allocation.periodDescription)}
                                                                    </p>
                                                                )}
                                                            </div>
                                                            <p className="font-semibold text-gray-900">
                                                                {formatCurrency(allocation.amount || allocation.clearedAmount || 0)}
                                                            </p>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                ) : (
                    <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                        <Receipt className="mx-auto h-16 w-16 mb-4 text-gray-300" />
                        <h4 className="text-lg font-medium text-gray-900 mb-2">No Payments Found</h4>
                        <p className="text-sm text-gray-500">There are no payment records for the selected date range.</p>
                    </div>
                )}

                {/* Structured Payments Breakdown */}
                {payments.length > 0 && (
                    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between gap-3 flex-wrap">
                            <div>
                                <h3 className="text-base font-semibold text-gray-900">Full Payments Breakdown</h3>
                                <p className="text-sm text-gray-600">Lot, method, references, and allocation drill-down.</p>
                            </div>
                        </div>
                        <div className="divide-y divide-gray-200">
                            {payments.map((payment, index) => {
                                const paymentLot = payment.paymentLot || payment.paymentLotId
                                const paymentLotItem = payment.paymentLotItem || payment.paymentLotItemId
                                const paymentReference = payment.paymentReference || payment.reference
                                const paymentTypeLabel = (payment.paymentDetails as any)?.paymentType || (payment as any).paymentType || payment.type || 'Payment'
                                const paymentMethodLabel = payment.paymentMethod || payment.method
                                const paymentDateValue = payment.paymentReceivedDate || payment.paymentDate || payment.date
                                const paymentStatus = payment.paymentStatus || payment.status
                                const paymentItems = payment.paymentItems || payment.items || []
                                const paymentAmount = payment.paymentAmount || payment.amount || 0

                                return (
                                    <div key={index} className="px-6 py-5">
                                        <div className="flex flex-wrap items-start justify-between gap-4">
                                            <div className="space-y-3">
                                                <div className="flex items-center gap-2">
                                                    <Receipt className="h-4 w-4 text-blue-600" />
                                                    <p className="text-sm font-semibold text-gray-900">
                                                        Payment Lot {String(paymentLot || '—')}
                                                    </p>
                                                    {paymentStatus ? (
                                                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100">
                                                            {String(paymentStatus)}
                                                        </span>
                                                    ) : null}
                                                </div>
                                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm text-gray-700">
                                                    <div>
                                                        <p className="text-xs uppercase tracking-wide text-gray-500">Lot Item</p>
                                                        <p className="font-semibold">{String(paymentLotItem || '—')}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs uppercase tracking-wide text-gray-500">Reference</p>
                                                        <p className="font-semibold">{String(paymentReference || '—')}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs uppercase tracking-wide text-gray-500">Payment Type</p>
                                                        <p className="font-semibold">{paymentTypeLabel}</p>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <div className="flex-1">
                                                            <p className="text-xs uppercase tracking-wide text-gray-500">Method</p>
                                                            <p className="font-semibold">{String(paymentMethodLabel || '—')}</p>
                                                        </div>
                                                        <div className="px-2 py-1 bg-green-50 border border-green-100 rounded-full text-[11px] text-green-700">
                                                            {formatDate(paymentDateValue as string)}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-xl font-bold text-green-600">{formatCurrency(Number(paymentAmount))}</p>
                                                <p className="text-xs text-gray-500 mt-1">Total applied</p>
                                            </div>
                                        </div>

                                        <div className="mt-4 space-y-3">
                                            {Array.isArray(paymentItems) && paymentItems.length > 0 ? (
                                                (paymentItems as any[]).map((item: any, itemIndex: number) => {
                                                    const allocations = item.allocations || []
                                                    return (
                                                        <div key={itemIndex} className="border border-gray-200 rounded-lg bg-gray-50 px-4 py-3">
                                                            <div className="flex flex-wrap justify-between items-start gap-3">
                                                                <div className="space-y-1">
                                                                    <div className="flex items-center gap-2">
                                                                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                                                                        <p className="text-sm font-semibold text-gray-900">
                                                                            {item.paymentItemType || item.type || 'Payment Item'}
                                                                        </p>
                                                                        {item.paymentItemStatus && (
                                                                            <span className="text-[11px] px-2 py-0.5 rounded-full bg-white border border-gray-200 text-gray-700">
                                                                                {String(item.paymentItemStatus)}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <div className="text-xs text-gray-600 flex flex-wrap gap-3">
                                                                        <span className="font-mono text-gray-500">ID: {item.paymentItemId || '—'}</span>
                                                                        <span className="flex items-center gap-1">
                                                                            <Clock className="h-3 w-3" />
                                                                            {formatDate(item.paymentItemDate as string)}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                                <div className="text-right">
                                                                    <p className="text-lg font-semibold text-gray-900">
                                                                        {formatCurrency(item.paymentItemAmount || item.amount || 0)}
                                                                    </p>
                                                                    <p className="text-xs text-gray-600">Item amount</p>
                                                                </div>
                                                            </div>

                                                            {allocations.length > 0 && (
                                                                <div className="mt-3">
                                                                    <p className="text-xs font-medium text-gray-700 mb-2">Allocations</p>
                                                                    <div className="space-y-2">
                                                                        {allocations.map((allocation: any, allocIndex: number) => (
                                                                            <div key={allocIndex} className="bg-white border border-gray-200 rounded px-3 py-2 flex items-start justify-between gap-3">
                                                                                <div>
                                                                                    <p className="text-sm font-semibold text-gray-900">
                                                                                        {allocation.chargeType || allocation.type || 'Charge'}
                                                                                    </p>
                                                                                    {allocation.chargeReference && (
                                                                                        <p className="text-[11px] font-mono text-gray-500">
                                                                                            {String(allocation.chargeReference)}
                                                                                        </p>
                                                                                    )}
                                                                                    {allocation.periodDescription && (
                                                                                        <p className="text-xs text-gray-600">
                                                                                            {String(allocation.periodDescription)}
                                                                                        </p>
                                                                                    )}
                                                                                </div>
                                                                                <p className="text-sm font-semibold text-gray-900">
                                                                                    {formatCurrency(allocation.amount || allocation.clearedAmount || 0)}
                                                                                </p>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )
                                                })
                                            ) : (
                                                <p className="text-sm text-gray-600">No payment items recorded for this payment.</p>
                                            )}
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                )}
            </div>
        )
    }

    return (
        <AuthenticatedLayout>
            <div className='flex h-full flex-col rounded-[15px] bg-white'>
              <div className='mx-auto flex h-full w-full max-w-[1600px] flex-col px-6 py-8 xl:px-32'>
                {/* Header */}
                <div className='mb-8 flex flex-shrink-0 items-center justify-between gap-3'>
                  <button
                    onClick={() => navigate('/dashboard')}
                    className='flex h-[34px] w-[34px] items-center justify-center rounded-full bg-gray-200 transition-colors hover:bg-gray-300'
                  >
                    <ArrowLeft className='h-5 w-5 text-gray-700' />
                  </button>
                  <div className='text-center'>
                    <h1 className='text-3xl font-bold text-gray-900'>Account Management</h1>
                    <p className='text-sm text-gray-600 mt-1'>View balance, transactions, and payment history</p>
                  </div>
                  <div className='h-[34px] w-[34px]' />
                </div>

                {/* Content */}
                <div className='flex-1 overflow-y-auto'>

                    {/* Search Form */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">Account Details</h2>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    National Insurance Number
                                </label>
                                <input
                                    value={nino}
                                    onChange={(e) => setNino(e.target.value)}
                                    placeholder="AB123456C"
                                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 font-medium focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <DatePicker
                                    id="fromDate"
                                    label="From Date"
                                    value={fromDate}
                                    onChange={setFromDate}
                                    placeholder="Select start date"
                                    className="w-full"
                                />
                                <DatePicker
                                    id="toDate"
                                    label="To Date"
                                    value={toDate}
                                    onChange={setToDate}
                                    placeholder="Select end date"
                                    className="w-full"
                                />
                            </div>
                            <button
                                onClick={setDefaultDates}
                                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                            >
                                Set Current Tax Year
                            </button>
                            <div className="flex flex-wrap gap-3">
                                <button
                                    onClick={handleLoadBalance}
                                    disabled={isLoadingBalance}
                                    className="px-6 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                    {isLoadingBalance ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            Loading...
                                        </>
                                    ) : (
                                        <>
                                            <Wallet className="h-4 w-4" />
                                            Load Balance
                                        </>
                                    )}
                                </button>
                                <button
                                    onClick={handleLoadPayments}
                                    disabled={isLoadingPayments}
                                    className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                    {isLoadingPayments ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            Loading...
                                        </>
                                    ) : (
                                        <>
                                            <DollarSign className="h-4 w-4" />
                                            Load Payments
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Messages */}
                    {error && (
                        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-red-800">{error}</p>
                        </div>
                    )}

                    {successMessage && (
                        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                            <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-green-800">{successMessage}</p>
                        </div>
                    )}

                    {/* View Tabs */}
                    {(balanceData || paymentsData) && (
                        <div className="mb-6">
                            <div className="border-b border-gray-200">
                                <nav className="-mb-px flex gap-6">
                                    <button
                                        onClick={() => setActiveView('balance')}
                                        className={`py-4 px-1 border-b-2 font-medium text-sm ${activeView === 'balance'
                                            ? 'border-green-600 text-green-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                            }`}
                                    >
                                        Balance & Transactions
                                    </button>
                                    <button
                                        onClick={() => setActiveView('payments')}
                                        className={`py-4 px-1 border-b-2 font-medium text-sm ${activeView === 'payments'
                                            ? 'border-blue-600 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                            }`}
                                    >
                                        Payments
                                    </button>
                                </nav>
                            </div>
                        </div>
                    )}

                    {/* Content */}
                    <div>
                        {activeView === 'balance' && renderBalanceView()}
                        {activeView === 'payments' && renderPaymentsView()}
                    </div>
                </div>
              </div>
            </div>
        </AuthenticatedLayout>
    )
}
